import React, { useState } from 'react';
import styled from 'styled-components';

import Text from 'components/common/Text';
import { IconChevronDown, IconTick } from 'assets/icons';

import { Option } from '../../../types/common.types';

const InputContainer = styled.div<{
  $isInputFocused: boolean;
  $isAbledToSearch: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 8px;

  width: 100%;
  height: 44px;
  padding: 0 12px;

  border-radius: 12px;

  border: ${({ $isInputFocused, $isAbledToSearch }) =>
    $isInputFocused ? ' 1.5px solid' : ' 1px solid'};
  border-color: ${({ $isInputFocused, $isAbledToSearch }) =>
    $isInputFocused
      ? 'rgba(65, 75, 86, 1)'
      : !$isAbledToSearch
      ? 'rgba(219, 220, 223, 1)'
      : 'rgba(234, 235, 236, 1)'};

  background-color: ${({ $isInputFocused, $isAbledToSearch }) =>
    $isInputFocused
      ? 'inherit'
      : $isAbledToSearch
      ? 'inherit'
      : 'rgba(247, 247, 248, 1)'};

  transition: border-color 0.3s ease-in-out;

  > input[type='text'] {
    width: 100%;
    height: 100%;

    border: none;

    background-color: inherit;

    /* background-color: ${props =>
      props.$isAbledToSearch ? 'inherit' : 'rgba(219, 220, 223, 1)'}; */
    color: #1b1c1e;
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;

    outline: none;

    &::placeholder {
      font-size: 14px;
      line-height: 20px;
      font-weight: 400;

      color: rgba(194, 196, 200, 1);
    }
  }
`;

const SearchKeywordSelect = styled.div`
  display: flex;
  align-items: center;

  position: relative;

  width: 100%;
  max-width: 68px;
  height: 100%;

  border: none;

  cursor: pointer;
`;

const ChevronBox = styled.div<{ $isSelectBoxOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  transform: rotate(${props => (props.$isSelectBoxOpen ? '180deg' : '0deg')});
  transition: transform 0.3s ease-in-out;
`;

const KeywordSelectListBox = styled.ul`
  position: absolute;
  top: 54px;
  left: -12px;
  z-index: 19;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;

  min-width: 200px;
  max-width: 200px;
  height: auto;

  padding: 16px 0px 20px;
  margin: 0;

  border-radius: 12px;

  background-color: white;

  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.15);

  overflow-y: hidden;

  > li {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;

    list-style: none;

    width: 100%;
    height: 44px;
    padding: 0 20px;

    background-color: white;
    font-weight: 500;
    color: rgba(27, 28, 30, 1);

    &.selected {
      color: rgba(0, 102, 255, 1);
    }

    &:hover {
      background-color: rgba(245, 246, 247, 1);
    }
  }
`;

const DividerBar = styled.div`
  width: 1px;
  height: 20px;
  background-color: rgba(219, 220, 223, 1);
`;

interface Props {
  keywordList: Option[];
  selectedKeywordOpt: Option;
  selectKeywordHandler: (opt: Option) => void;
  selectPlaceHandler?: string;
  inputPlaceHandler?: string;
  isAbledToSearch: boolean;
  searchText: string;
  inputHandler: (value: string) => void;
  submitHandler: () => void;
}

function KeywordSearchSelectInput({
  keywordList,
  selectedKeywordOpt,
  selectKeywordHandler,
  selectPlaceHandler,
  searchText,
  inputHandler,
  inputPlaceHandler,
  isAbledToSearch,
  submitHandler,
}: Props) {
  const [isSelectBoxOpen, setIsSelectBoxOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const inputFocusedHandler = () => {
    setIsInputFocused(prev => !prev);
  };

  const handleSelectBoxOpen = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();

    setIsSelectBoxOpen(prev => !prev);
  };

  return (
    <InputContainer
      $isInputFocused={isInputFocused}
      $isAbledToSearch={isAbledToSearch}
    >
      <SearchKeywordSelect onClick={handleSelectBoxOpen}>
        <Text
          weight={400}
          size="14px"
          color="rgba(194, 196, 200, 1)"
          style={{ width: '50px' }}
        >
          {selectedKeywordOpt.label || selectPlaceHandler || '검색 옵션'}
        </Text>

        <ChevronBox $isSelectBoxOpen={isSelectBoxOpen}>
          <IconChevronDown color="rgba(194, 196, 200, 1)" />
        </ChevronBox>

        {isSelectBoxOpen && (
          <KeywordSelectListBox>
            {keywordList.map(opt => {
              const isSelected = selectedKeywordOpt.value === opt.value;

              return (
                <li
                  key={opt.value}
                  data-keyword={opt.value}
                  className={isSelected ? 'selected' : ''}
                  onClick={() => selectKeywordHandler(opt)}
                >
                  {opt.label}

                  {isSelected && <IconTick />}
                </li>
              );
            })}
          </KeywordSelectListBox>
        )}
      </SearchKeywordSelect>

      <DividerBar />

      <input
        type={'text'}
        placeholder={inputPlaceHandler || '검색어를 입력해주세요.'}
        value={searchText}
        onChange={e => inputHandler(e.target.value)}
        onFocus={inputFocusedHandler}
        onBlur={inputFocusedHandler}
        onKeyDown={e => {
          e.stopPropagation();
          if (e.key === 'Enter') {
            return submitHandler();
          }
        }}
      />
    </InputContainer>
  );
}

export default KeywordSearchSelectInput;
